import { GameState } from "../types/game";
import { StreamChatMessage, ChatMessageKind, ChatAvatar } from "../types/chat";
import { assetUrl } from "./assets";

type ChatProfile = {
  name: string;
  initials: string;
  from: string;
  to: string;
  imageSrc: string;
};

const CHAT_PROFILES: readonly ChatProfile[] = [
  {
    name: "MiaK",
    initials: "MK",
    from: "#fb7185",
    to: "#f97316",
    imageSrc: assetUrl("pfps/peepo.png"),
  },
  {
    name: "Luca",
    initials: "LU",
    from: "#2dd4bf",
    to: "#0ea5e9",
    imageSrc: assetUrl("pfps/noot.jpg"),
  },
  {
    name: "Nori",
    initials: "NO",
    from: "#c084fc",
    to: "#60a5fa",
    imageSrc: assetUrl("pfps/kappa.png"),
  },
  {
    name: "Jayden",
    initials: "JY",
    from: "#facc15",
    to: "#f97316",
    imageSrc: assetUrl("pfps/luffy.jpg"),
  },
  {
    name: "Ari",
    initials: "AR",
    from: "#34d399",
    to: "#10b981",
    imageSrc: assetUrl("pfps/catJam.jpg"),
  },
  {
    name: "Sage",
    initials: "SG",
    from: "#a78bfa",
    to: "#f472b6",
    imageSrc: assetUrl("pfps/peepo.png"),
  },
  {
    name: "Leni",
    initials: "LE",
    from: "#38bdf8",
    to: "#818cf8",
    imageSrc: assetUrl("pfps/noot.jpg"),
  },
  {
    name: "NoahQ",
    initials: "NQ",
    from: "#fb7185",
    to: "#a78bfa",
    imageSrc: assetUrl("pfps/kappa.png"),
  },
  {
    name: "Rin",
    initials: "RN",
    from: "#4ade80",
    to: "#22d3ee",
    imageSrc: assetUrl("pfps/luffy.jpg"),
  },
  {
    name: "Timo",
    initials: "TM",
    from: "#fda4af",
    to: "#f59e0b",
    imageSrc: assetUrl("pfps/catJam.jpg"),
  },
];

const EMOTE_POOL = [
  ":catJam:",
  ":monkaS:",
  ":peepoArrive:",
  ":peepoHappy:",
  ":peepoShy:",
  ":pepeJAM:",
  ":pepeLaugh:",
  ":popCat:",
];

const pick = <T>(items: readonly T[]): T =>
  items[Math.floor(Math.random() * items.length)];

const buildContext = (state: GameState) => {
  const slots = state.avatarState.slots;

  return {
    hasMusic: state.unlocks.music.lofi,
    hasSmile: Boolean(slots.eyes || slots.mouth),
    hasBlush: Boolean(slots.blush),
    hasHair: Boolean(slots.hair),
    hasBackflip: Boolean(state.unlocks.animations.Backflip),
    hasPose: Boolean(state.unlocks.animations.Pose_confident),
    hasAura: Boolean(state.unlocks.animations.AuraFarmer),
    hasDance: Boolean(state.unlocks.animations.Dance),
    title: state.streamTitle.value.trim(),
    hasTitle:
      state.streamTitle.unlocked && state.streamTitle.value.trim().length > 0,
  };
};

const createAvatar = (profile: ChatProfile): ChatAvatar => ({
  initials: profile.initials,
  from: profile.from,
  to: profile.to,
  imageSrc: profile.imageSrc,
});

const withOptionalEmote = (text: string, kind: ChatMessageKind) => {
  const shouldAdd =
    kind === "morale"
      ? Math.random() < 0.6
      : kind === "nice"
        ? Math.random() < 0.45
        : Math.random() < 0.35;

  if (!shouldAdd) return text;
  return `${text} ${pick(EMOTE_POOL)}`;
};

const buildPositivePool = (
  state: GameState,
  kind: Extract<ChatMessageKind, "morale" | "nice">,
) => {
  const ctx = buildContext(state);
  const generic =
    kind === "morale"
      ? [
          "W stream",
          "we are so back",
          "keep going this is fire",
          "chat wake up this is a run",
          "lets gooo",
          "big W energy",
        ]
      : [
          "this is fun to watch",
          "im staying for a while",
          "the vibe is nice here",
          "chat is chill today",
          "youre doing great",
          "this stream lowkey cozy",
        ];

  const appearance: string[] = [];
  if (ctx.hasSmile) {
    appearance.push(
      "that smile is looking good",
      "okay the face card never declines",
      "the expression looks cute",
    );
  }
  if (ctx.hasBlush) {
    appearance.push(
      "the blush is such a good touch",
      "the blush makes this way more charming",
    );
  }
  if (ctx.hasHair) {
    appearance.push(
      "the hair is actually iconic",
      "okay the hairstyle is carrying",
    );
  }

  const music: string[] = ctx.hasMusic
    ? [
        "this lofi is perfect for the stream",
        "music choice is elite",
        "the lofi loop is hitting",
      ]
    : [];

  const animation: string[] = [];
  if (ctx.hasDance) {
    animation.push("chat wants the dance again", "dance button when");
  }
  if (ctx.hasBackflip) {
    animation.push(
      "the backflip still goes crazy",
      "backflip is still a classic",
    );
  }
  if (ctx.hasAura) {
    animation.push("aura farmer is unreal", "that aura move is busted");
  }
  if (ctx.hasPose) {
    animation.push("the confident pose is clean", "pose timing goes hard");
  }

  const title: string[] = ctx.hasTitle
    ? [
        `"${ctx.title}" is such a funny title`,
        `the title "${ctx.title}" got me clicking`,
      ]
    : [];

  return [...generic, ...appearance, ...music, ...animation, ...title];
};

const buildBadPool = (state: GameState) => {
  const ctx = buildContext(state);
  const generic = [
    "this stream kinda dry",
    "chat moving weird today",
    "im not seeing the vision",
    "why is chat so dead rn",
    "this needs more energy",
  ];

  const appearance: string[] = [];
  if (ctx.hasSmile || ctx.hasBlush || ctx.hasHair) {
    appearance.push(
      "the look is not really doing it for me",
      "chat am i the only one not feeling the fit",
    );
  }

  const music: string[] = ctx.hasMusic
    ? ["this music loop is getting old", "who put this song on repeat"]
    : [];

  const animation: string[] = [];
  if (ctx.hasDance || ctx.hasBackflip || ctx.hasAura || ctx.hasPose) {
    animation.push(
      "same moves again?",
      "the stream needs a better moment than that",
    );
  }

  const title: string[] = ctx.hasTitle
    ? [
        `that title "${ctx.title}" is kinda wild`,
        "the stream title is not helping",
      ]
    : [];

  return [...generic, ...appearance, ...music, ...animation, ...title];
};

export const createGeneratedChatMessage = (
  state: GameState,
  kind: "morale" | "nice" | "bad",
  penaltyViewers = 0,
): StreamChatMessage => {
  const profile = pick(CHAT_PROFILES);
  const text =
    kind === "bad"
      ? pick(buildBadPool(state))
      : pick(buildPositivePool(state, kind));

  return {
    id: crypto.randomUUID(),
    kind,
    author: profile.name,
    text: withOptionalEmote(text, kind),
    avatar: createAvatar(profile),
    ttlMs: kind === "bad" ? 11000 : 9000,
    penaltyViewers: penaltyViewers > 0 ? penaltyViewers : undefined,
  };
};
